<template>
  <div class="transfer-shipping-box">
    <!-- Header Section -->
    <div class="transfer-shipping-box__header">
      <h2 class="transfer-shipping-box__title">{{ headerText }}</h2>
    </div>

    <!-- Delivery Date Section -->
    <div
      :id="deliveryContainerId"
      class="transfer-shipping-box__delivery-section js-product-details-delivery-dates-simple-wrapper"
      :data-product-id="productSlug"
      data-selected-color="Transfer"
      :data-is-dtf="isDtfSheet"
      :data-enabled-shipping-enhancement-services="experiments.ab_shipping_enhancement_services > 0"
      data-assignable="true"
    >
      <!-- Loading State -->
      <div
        class="transfer-shipping-box__loading js-product-details-delivery-dates-simple-spinner"
        :class="{ 'transfer-shipping-box__loading--hidden': !isShippingLoading }"
      >
        <div class="transfer-shipping-box__loading-content">
          <span class="transfer-shipping-box__loading-main-text">
            Calculating shipping options
          </span>
          <div class="transfer-shipping-box__loading-steps">
            <transition name="fade" mode="out-in">
              <span :key="currentLoadingStep" class="transfer-shipping-box__loading-step-text">
                {{ loadingSteps[currentLoadingStep] }}
              </span>
            </transition>
          </div>
        </div>
        <slot name="dates"></slot>
      </div>

      <!-- Loaded State -->
      <div
        class="transfer-shipping-box__delivery-wrapper"
        :class="{ 'transfer-shipping-box__delivery-wrapper--hidden': isShippingLoading }"
      >
        <div class="transfer-shipping-box__delivery-content">
          <div class="transfer-shipping-box__delivery-info">
            <!-- Normal state icon (box with checkmark) -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              class="transfer-shipping-box__delivery-icon transfer-shipping-box__delivery-icon--box"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M11.5973 1.18499C11.8632 1.13031 12.1373 1.13031 12.4032 1.18498C12.7104 1.24818 12.9851 1.40201 13.2034 1.5243C13.2237 1.53569 13.2436 1.54681 13.2629 1.55755L12.7848 2.41817L13.2629 1.55755L20.6629 5.66866C20.6834 5.68002 20.7045 5.69163 20.7261 5.70353C20.9572 5.83084 21.2478 5.99091 21.471 6.23309L21.1565 6.5229L21.471 6.23309C21.6639 6.44247 21.81 6.69064 21.8993 6.96099C22.0026 7.27368 22.0014 7.60547 22.0004 7.86934C22.0003 7.894 22.0002 7.91807 22.0002 7.94147L22.0002 12.5C22.0002 13.0523 21.5525 13.5 21.0002 13.5C20.448 13.5 20.0002 13.0523 20.0002 12.5L20.0002 8.69949L17.0031 10.3646C16.9923 10.3709 16.9813 10.3769 16.9703 10.3828L13.0002 12.5884L13.0002 20.3005L13.5146 20.0148C13.9974 19.7465 14.6062 19.9205 14.8744 20.4033C15.1426 20.8861 14.9687 21.4949 14.4859 21.7631L13.2629 22.4425C13.2436 22.4532 13.2237 22.4644 13.2034 22.4757C12.9851 22.598 12.7104 22.7519 12.4031 22.8151C12.1373 22.8697 11.8632 22.8697 11.5974 22.8151C11.2901 22.7519 11.0154 22.598 10.7971 22.4757C10.7768 22.4644 10.7569 22.4532 10.7376 22.4425L3.33758 18.3314C3.31712 18.32 3.29605 18.3084 3.27445 18.2965C3.04332 18.1692 2.75269 18.0091 2.52951 17.767C2.33656 17.5576 2.19054 17.3094 2.10122 17.0391C1.9979 16.7264 1.99911 16.3946 2.00007 16.1307C2.00016 16.106 2.00025 16.082 2.00025 16.0586V7.94147C2.00025 7.91807 2.00016 7.894 2.00007 7.86934C1.99911 7.60547 1.9979 7.27369 2.10122 6.96099C2.19054 6.69063 2.33656 6.44247 2.52951 6.23309C2.75269 5.99092 3.0433 5.83084 3.27443 5.70354C3.29603 5.69164 3.31711 5.68003 3.33757 5.66866L3.33757 5.66866L6.99929 3.63438C7.00886 3.62885 7.01852 3.62348 7.02826 3.61828L10.7376 1.55755C10.7569 1.54681 10.7768 1.53569 10.7971 1.5243C11.0154 1.40201 11.2901 1.24818 11.5973 1.18499ZM7.50025 5.64398L5.05938 7.00002L12.0002 10.856L14.4411 9.5L7.50025 5.64398ZM16.5002 8.35604L9.55937 4.50002L11.7089 3.30586C11.8604 3.22168 11.9378 3.17914 11.9955 3.1513C11.9971 3.1505 11.9987 3.14974 12.0003 3.14901C12.0018 3.14974 12.0034 3.1505 12.005 3.1513C12.0627 3.17913 12.1401 3.22167 12.2916 3.30586L18.9411 7L16.5002 8.35604ZM11.0002 12.5884L4.00025 8.69953V16.0586C4.00025 16.2416 4.00066 16.3352 4.00475 16.403C4.00487 16.405 4.00499 16.4069 4.00511 16.4088C4.00665 16.4097 4.00826 16.4108 4.00995 16.4119C4.06725 16.4484 4.14891 16.4942 4.30886 16.5831L3.82322 17.4572L4.30886 16.5831L11.0002 20.3005L11.0002 12.5884ZM22.7074 15.2929C23.0979 15.6834 23.0979 16.3166 22.7074 16.7071L18.7074 20.7071C18.3168 21.0977 17.6837 21.0977 17.2931 20.7071L15.2931 18.7071C14.9026 18.3166 14.9026 17.6834 15.2931 17.2929C15.6837 16.9024 16.3168 16.9024 16.7074 17.2929L18.0002 18.5858L21.2931 15.2929C21.6837 14.9024 22.3168 14.9024 22.7074 15.2929Z"
                fill="currentColor"
              />
            </svg>
            <!-- Same-day state icon (zap-fast) -->
            <UseSvg
              icon="zap-fast"
              class="transfer-shipping-box__delivery-icon transfer-shipping-box__delivery-icon--same-day"
            />
            <span class="transfer-shipping-box__delivery-date-label">
              <span class="transfer-shipping-box__delivery-date-prefix">Get it</span>
              <span
                class="transfer-shipping-box__delivery-date-value js-product-details-delivery-dates-simple-direct-value"
              ></span>
            </span>
          </div>

          <div
            class="transfer-shipping-box__cutoff js-product-details-delivery-dates-simple-cutoff-wrapper"
          >
            <span class="transfer-shipping-box__cutoff-prefix">If ordered in</span>
            <span
              class="transfer-shipping-box__cutoff-time js-product-details-delivery-dates-cutoff"
            >
              ...
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Free Shipping Banner -->
    <div class="transfer-shipping-box__shipping-banner">
      <!-- Normal delivery format -->
      <div
        class="transfer-shipping-box__shipping-content transfer-shipping-box__shipping-content--normal js-transfer-shipping-normal"
      >
        <span class="transfer-shipping-box__shipping-text">
          <span class="transfer-shipping-box__shipping-highlight">FREE SHIPPING</span>
          <span class="transfer-shipping-box__shipping-at">at</span>
          <span
            class="transfer-shipping-box__shipping-price js-made-product-details-delivery-dates-simple-free-shipping-value"
            >{{ formattedShippingThreshold }}</span
          >
        </span>
        <span class="transfer-shipping-box__shipping-separator">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="6"
            height="5"
            viewBox="0 0 6 5"
            fill="none"
          >
            <circle cx="3.00024" cy="2.5" r="2.5" fill="#BBBFC8" />
          </svg>
        </span>
        <span class="transfer-shipping-box__shipping-note"> No Hidden Rush Fees </span>
      </div>

      <!-- Same-day delivery format -->
      <div
        class="transfer-shipping-box__shipping-content transfer-shipping-box__shipping-content--same-day js-transfer-shipping-same-day"
        style="display: none"
      >
        <span class="transfer-shipping-box__shipping-text">
          <span class="transfer-shipping-box__shipping-free">FREE</span>
          <span class="transfer-shipping-box__shipping-standard"> Standard Shipping</span>
          <span class="transfer-shipping-box__shipping-at"> At </span>
          <span
            class="transfer-shipping-box__shipping-price js-made-product-details-delivery-dates-simple-free-shipping-value"
            >{{ formattedShippingThreshold }}</span
          >
        </span>
        <span class="transfer-shipping-box__shipping-separator"> or </span>
      </div>
    </div>

    <!-- Features Section -->
    <div class="transfer-shipping-box__features">
      <div class="transfer-shipping-box__feature">
        <UseSvg icon="check-circle" class="transfer-shipping-box__feature-icon" />
        <span class="transfer-shipping-box__feature-text">Easy to press</span>
      </div>
      <div class="transfer-shipping-box__feature">
        <UseSvg icon="check-circle" class="transfer-shipping-box__feature-icon" />
        <span class="transfer-shipping-box__feature-text">Vibrant Colors</span>
      </div>
      <div class="transfer-shipping-box__feature">
        <UseSvg icon="check-circle" class="transfer-shipping-box__feature-icon" />
        <span class="transfer-shipping-box__feature-text">Premium Quality</span>
      </div>
    </div>

    <!-- Pro Color Upsell Section -->
    <div v-if="showProColorUpsell" class="transfer-shipping-box__pro-color">
      <ProColorUpsell :artwork="artwork" class="transfer-shipping-box__pro-color-component" />
    </div>
  </div>
</template>

<script>
import { mapState } from "pinia";
import { moneyFormat } from "common/utils/filters/money";
import { deliveryDateResponse } from "components/jiffy/product-details-delivery-date-simple/init-dates";
import logger from "common/utils/logger";
import UseSvg from "../vue-cart/UseSvg.vue";
import ProColorUpsell from "./ProColorUpsell.vue";
import { useDtfStore } from "./store";

export default {
  name: "TransferShippingBox",
  components: {
    UseSvg,
    ProColorUpsell
  },
  props: {
    artwork: {
      type: [Object, null],
      default: null
    },
    showProColorUpsell: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentLoadingStep: 0,
      loadingInterval: null,
      loadingObserver: null,
      isShippingLoading: true, // Start with true to show loading by default
      shippingThreshold: null,
      deliveryType: null, // 'same-day', 'next-day', or 'standard'
      loadingSteps: [
        "Checking inventory availability",
        "Calculating delivery times",
        "Finding best shipping rates",
        "Verifying carrier options",
        "Optimizing delivery routes"
      ]
    };
  },

  computed: {
    ...mapState(useDtfStore, ["isDtfSheet", "productSlug", "experiments"]),

    deliveryContainerId() {
      // Always use the same ID that the external system expects
      return "js-product-details-delivery-dates-simple-container";
    },

    formattedShippingThreshold() {
      if (!this.shippingThreshold) {
        return "...";
      }

      // Format threshold as currency
      if (typeof this.shippingThreshold === "number") {
        return moneyFormat(this.shippingThreshold);
      }
      if (typeof this.shippingThreshold === "string" && !this.shippingThreshold.includes("$")) {
        return moneyFormat(parseFloat(this.shippingThreshold));
      }

      return this.shippingThreshold;
    },

    headerText() {
      // Default text when no delivery info available
      if (!this.deliveryType) {
        return "DTF TRANSFERS BY SIZE";
      }

      // Dynamic text based on delivery type
      if (this.deliveryType === "same-day") {
        return "Order now. Delivered Today.";
      }
      if (this.deliveryType === "next-day") {
        return "Order now. Delivered Tomorrow.";
      }
      return "DTF TRANSFERS BY SIZE";
    }
  },
  watch: {
    isShippingLoading(newValue, oldValue) {
      // When shipping loading transitions from true to false, fetch delivery info
      if (oldValue === true && newValue === false) {
        this.fetchDeliveryInfo();
      }
    }
  },
  mounted() {
    this.startLoadingAnimation();
    this.$nextTick(() => {
      this.observeExternalLoadingState();

      // Check if delivery date response is already available
      if (deliveryDateResponse.lastResponse) {
        this.fetchDeliveryInfo();
      }
    });
  },
  beforeUnmount() {
    this.stopLoadingAnimation();
    if (this.loadingObserver) {
      this.loadingObserver.disconnect();
      this.loadingObserver = null;
    }
  },
  methods: {
    startLoadingAnimation() {
      this.loadingInterval = setInterval(() => {
        this.currentLoadingStep = (this.currentLoadingStep + 1) % this.loadingSteps.length;
      }, 2000); // Change step every 2 seconds
    },
    stopLoadingAnimation() {
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
    },
    observeExternalLoadingState() {
      // Wait a bit for external JS to potentially modify the DOM
      setTimeout(() => {
        const loadingSpinner = this.$el.querySelector(
          ".js-product-details-delivery-dates-simple-spinner"
        );

        if (loadingSpinner) {
          // Check if external system has already added the shown class
          const hasShownClass = loadingSpinner.classList.contains("shown");

          // Only change loading state if external system has explicitly set shown class
          // If no shown class yet, keep showing loading until external system takes over
          if (hasShownClass) {
            this.isShippingLoading = true;
          }

          // Set up observer for future changes
          const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
              if (mutation.type === "attributes" && mutation.attributeName === "class") {
                const hasShown = loadingSpinner.classList.contains("shown");
                this.isShippingLoading = hasShown;
              }
            });
          });

          observer.observe(loadingSpinner, {
            attributes: true,
            attributeFilter: ["class"]
          });

          this.loadingObserver = observer;
        }
      }, 100);
    },

    fetchDeliveryInfo() {
      const response = deliveryDateResponse.lastResponse;

      if (!response) {
        return;
      }

      const shippingCostInfo =
        response.cart_dates?.shipping_cost_info || response.composition_dates?.shipping_cost_info;
      const valueFull = response.cart_dates?.value_full || response.composition_dates?.value_full;

      if (shippingCostInfo?.free_shipping_threshold) {
        this.shippingThreshold = shippingCostInfo.free_shipping_threshold;
      } else {
        logger.log("[TransferShippingBox] No free_shipping_threshold found in response");
      }

      // Determine delivery type based on value_full text (matching init-dates.js logic)
      if (valueFull) {
        const deliveryText = valueFull.toLowerCase();

        if (deliveryText.includes("today")) {
          this.deliveryType = "same-day";
        } else if (deliveryText.includes("tomorrow")) {
          this.deliveryType = "next-day";
        } else {
          this.deliveryType = "standard";
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "frontend/init/jiffy/_settings.scss";
@import "frontend/init/jiffy/_mixin.scss";

.transfer-shipping-box {
  $this: &;

  background: $white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid #e5e7eb;
  min-height: 224px;

  @include with-tablet {
    min-height: 230px;
  }

  &__header {
    background: #c5e3ff;
    padding: 10px 16px;
    text-align: center;
  }

  &__title {
    color: #1a1a1a;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    margin: 0;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  &__delivery-section {
    padding: 20px 16px;
    min-height: 80px;
    position: relative;

    // Mobile-specific padding adjustments
    @include only-mobile {
      padding: 16px 12px;
    }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    &--hidden {
      display: none !important;
    }
  }

  &__loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  &__loading-main-text {
    color: #333;
    font-size: 16px;
    font-weight: 600;
  }

  &__loading-steps {
    min-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__loading-step-text {
    color: #666;
    font-size: 14px;
    font-weight: 400;
    text-align: center;
  }

  &__delivery-wrapper {
    text-align: center;

    &.hidden {
      display: none !important;
    }

    &--hidden {
      display: none !important;
    }
  }

  &__delivery-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 4px;
  }

  &__delivery-icon {
    width: 32px;
    height: 32px;
    color: #333;

    &--box {
      display: block;
    }

    &--same-day {
      color: #dd3622;
      display: none; // Hidden by default
    }
  }

  &__delivery-date-label {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
  }

  &__delivery-date-prefix {
    color: #25272c;
    font-size: 28px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 24px;
      line-height: 28px;
    }
  }

  &__delivery-date-value {
    color: #1d4ed8;
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 24px;
      line-height: 28px;
    }
  }

  &__cutoff {
    font-size: 13px;
    margin-top: 4px;
    line-height: 20px;
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 4px;

    // Mobile-specific font size increase
    @include only-mobile {
      font-size: 15px;
      line-height: 22px;
    }
  }

  &__cutoff-prefix {
    color: #363c44;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;

    // Mobile-specific font size increase
    @include only-mobile {
      font-size: 15px;
      line-height: 22px;
    }
  }

  &__cutoff-time {
    color: #363c44;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;

    // Mobile-specific font size increase
    @include only-mobile {
      font-size: 15px;
      line-height: 22px;
    }
  }

  &__shipping-banner {
    padding: 12px 16px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;

    // Mobile-specific adjustments
    @include only-mobile {
      padding: 12px;
      gap: 6px;
    }
  }

  &__shipping-text {
    font-size: 16px;
    line-height: normal;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-highlight {
    color: #1d4ed8;
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-at {
    color: #25272c;
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-price {
    color: #25272c;
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-separator {
    display: flex;
    align-items: center;
    justify-content: center;

    // SVG styles (normal delivery)
    svg {
      fill: #bbbfc8;
      width: 5px;
      height: 5px;
      flex-shrink: 0;
    }
  }

  // Same-day specific separator styling
  &__shipping-content--same-day &__shipping-separator {
    position: absolute;
    top: -23px;
    left: 50%;
    transform: translateX(-50%);
    color: #25272c;
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    background: white;
    padding: 0 8px;
    z-index: 1;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-note {
    color: #0e0e0e;
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-delivery-date {
    color: #25272c;
    font-feature-settings:
      "liga" off,
      "clig" off;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    // Mobile-specific font size reduction
    @include only-mobile {
      font-size: 13px;
    }
  }

  &__shipping-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    text-align: center;

    &--normal {
      flex-direction: row;
    }

    &--same-day {
      // Same-day delivery content styling - center everything
      flex-direction: column;
      gap: 0;
      position: relative;
    }
  }

  // Same-day delivery specific styles
  &__shipping-content--same-day &__shipping-text {
    text-align: center;
    width: 100%;
    display: block;
    .transfer-shipping-box__shipping-free {
      color: #25272c;
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;

      // Mobile-specific font size reduction
      @include only-mobile {
        font-size: 13px;
      }
    }

    .transfer-shipping-box__shipping-standard {
      color: #25272c;
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;

      // Mobile-specific font size reduction
      @include only-mobile {
        font-size: 13px;
      }
    }

    .transfer-shipping-box__shipping-at {
      color: #25272c;
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;

      // Mobile-specific font size reduction
      @include only-mobile {
        font-size: 13px;
      }
    }

    .transfer-shipping-box__shipping-price {
      color: #25272c;
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;

      // Mobile-specific font size reduction
      @include only-mobile {
        font-size: 13px;
      }
    }

    .transfer-shipping-box__shipping-delivery-date {
      color: #25272c;
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;

      // Mobile-specific font size reduction
      @include only-mobile {
        font-size: 13px;
      }
    }
  }

  &__features {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 8px 0px;
    gap: 32px;
    flex-wrap: wrap;

    // Mobile-specific styles - show only 2 features (hide Premium Quality)
    @include only-mobile {
      gap: 24px;
      padding: 8px 8px;
      flex-wrap: nowrap;
      justify-content: center;
    }

    // Hide the third feature (Premium Quality) on mobile
    @include only-mobile {
      .transfer-shipping-box__feature:nth-child(3) {
        display: none;
      }
    }
  }

  &__feature {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    text-align: center;
    margin: 0;
    padding: 0;

    // Mobile-specific styles for better fit (optimized for 2 features)
    @include only-mobile {
      gap: 8px;
      flex-shrink: 0;
    }
  }

  &__feature-icon {
    width: 20px;
    height: 20px;
    color: #1d4ed8;
    flex-shrink: 0;
    margin: 0;
  }

  &__feature-text {
    font-size: 14px;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      Roboto,
      sans-serif;
    color: #111827;
    white-space: nowrap;
    font-weight: 600;
    line-height: 16.8px;
    margin: 0;
    padding: 0;
    font-feature-settings:
      "liga" off,
      "clig" off;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    // Mobile-specific styles for better text fitting (optimized for 2 features)
    @include only-mobile {
      font-size: 14px;
      line-height: 16.8px;
      white-space: nowrap;
    }
  }

  &__pro-color {
    border-top: 1px solid #e5e7eb;

    :deep(.pro-color-upsell) {
      border: none;
    }
  }

  &__pro-color-component {
    margin: 0;
    padding: 20px;

    // Mobile-specific Pro Color section styling
    @include only-mobile {
      padding: 16px 12px;
    }
  }
}

// Same Day Styling - Applied when external JS adds the class to the container
// Using :has() for modern browsers
.transfer-shipping-box:has(
    #js-product-details-delivery-dates-simple-container.product-details-delivery-date-simple--same-day
  ) {
  .transfer-shipping-box__header {
    background: #fcc8bc !important;
  }

  .transfer-shipping-box__feature-icon {
    color: #dd3622 !important;
  }
}

// Fallback approach - target specific elements directly when same-day class is present
#js-product-details-delivery-dates-simple-container.product-details-delivery-date-simple--same-day {
  // Elements inside the delivery container
  .transfer-shipping-box__delivery-date-value {
    color: #dd3622 !important;
  }

  .transfer-shipping-box__delivery-icon {
    &--box {
      display: none; // Hide box icon in same-day mode
    }

    &--same-day {
      display: block; // Show zap icon in same-day mode
    }
  }

  // Target header and features with more specific selectors
  ~ * .transfer-shipping-box__header,
  + * .transfer-shipping-box__header,
  .transfer-shipping-box__header {
    background: #fcc8bc !important;
  }

  ~ * .transfer-shipping-box__feature-icon,
  + * .transfer-shipping-box__feature-icon,
  .transfer-shipping-box__feature-icon {
    color: #dd3622 !important;
  }
}

// Alternative: Use adjacent/general sibling selectors to target elements after the container
#js-product-details-delivery-dates-simple-container.product-details-delivery-date-simple--same-day
  ~ .transfer-shipping-box__features
  .transfer-shipping-box__feature-icon {
  color: #dd3622 !important;
}

// Toggle shipping banner visibility based on same-day delivery
// When same-day delivery is active, hide normal banner and show same-day banner

// Primary method: Using :has() selector for modern browsers
.transfer-shipping-box:has(.product-details-delivery-date-simple--same-day) {
  .js-transfer-shipping-normal {
    display: none !important;
  }

  .js-transfer-shipping-same-day {
    display: flex !important;
  }
}

// Fallback method 1: Direct parent-child relationship
.product-details-delivery-date-simple--same-day {
  // Go up to parent delivery section, then to its sibling shipping banner
  & ~ .transfer-shipping-box__shipping-banner,
  & + .transfer-shipping-box__shipping-banner {
    .js-transfer-shipping-normal {
      display: none !important;
    }

    .js-transfer-shipping-same-day {
      display: flex !important;
    }
  }
}

// Fallback method 2: Target from the parent container
.transfer-shipping-box__delivery-section {
  &:has(.product-details-delivery-date-simple--same-day) {
    & + .transfer-shipping-box__shipping-banner {
      .js-transfer-shipping-normal {
        display: none !important;
      }

      .js-transfer-shipping-same-day {
        display: flex !important;
      }
    }
  }
}

// Global hidden class to ensure it takes precedence
.hidden {
  display: none !important;
}

// Fade transition for loading steps
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
